import { useState } from "react";
import { Bell, Send, Slack, Mail } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

// ponytail: UI mockup only — alert delivery backend isn't built yet. Local
// state, no persistence. Real channel secrets (bot token / webhook) must live
// server-side (vault / a column not granted to the browser), never shipped here.

type Channel = { id: string; title: string; placeholder: string; icon: typeof Bell };

const CHANNELS: Channel[] = [
  { id: "telegram", title: "Telegram", placeholder: "Bot token + chat id", icon: Send },
  { id: "slack", title: "Slack", placeholder: "Incoming webhook URL", icon: Slack },
  { id: "email", title: "Email", placeholder: "alerts@oner.team", icon: Mail },
];

const KINDS = [
  { id: "dmca", title: "New DMCA takedown", desc: "A domain or URL gets a Google removal notice." },
  { id: "index_drop", title: "Index drop", desc: "A page that was indexed is no longer indexed." },
  { id: "redirect", title: "Redirect detected", desc: "A domain/URL now redirects elsewhere." },
] as const;

export function AlertsSettings() {
  const [channels, setChannels] = useState<Record<string, boolean>>({
    telegram: true,
    slack: false,
    email: false,
  });
  const [values, setValues] = useState<Record<string, string>>({});
  const [kinds, setKinds] = useState<Record<string, boolean>>({
    dmca: true,
    index_drop: true,
    redirect: false,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Badge variant="outline">UI only</Badge>
        <span className="text-sm text-muted-foreground">
          Alert delivery isn't built yet — these controls don't persist. Channel
          secrets will live server-side, not in the browser.
        </span>
      </div>

      {/* Channels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="size-4" /> Channels
          </CardTitle>
          <CardDescription>Where alerts are delivered.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {CHANNELS.map((c) => (
            <div key={c.id} className="flex items-center gap-3">
              <Switch
                checked={channels[c.id]}
                onCheckedChange={(v) => setChannels((cur) => ({ ...cur, [c.id]: v }))}
                aria-label={c.title}
              />
              <div className="flex w-32 items-center gap-2">
                <c.icon className="size-4 text-muted-foreground" />
                <Label className="font-medium">{c.title}</Label>
              </div>
              <Input
                placeholder={c.placeholder}
                value={values[c.id] ?? ""}
                onChange={(e) => setValues((cur) => ({ ...cur, [c.id]: e.target.value }))}
                disabled={!channels[c.id]}
                className="max-w-[360px]"
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* What triggers an alert */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">What triggers an alert</CardTitle>
          <CardDescription>Which events get sent to the enabled channels.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {KINDS.map((k) => (
            <div key={k.id} className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <Label htmlFor={`kind-${k.id}`} className="font-medium">{k.title}</Label>
                <p className="text-sm text-muted-foreground">{k.desc}</p>
              </div>
              <Switch
                id={`kind-${k.id}`}
                checked={kinds[k.id]}
                onCheckedChange={(v) => setKinds((cur) => ({ ...cur, [k.id]: v }))}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Button className="cursor-pointer" disabled title="Wired when alert delivery lands">
        Save changes
      </Button>
    </div>
  );
}
